import fp from 'lodash/fp';

import { Dictionary } from './dictionary';
import { Common } from './common';
import { API } from './api';


export namespace Filters {
    export interface FilterDefinition extends Dictionary.DictionaryItem {
        Mask: number;
    }

    export interface FilterDictionary extends Common.Catalog<FilterDefinition> {}

    export interface FilterValue<TValue = any> {
        value: TValue;
    }

    export interface MaskFilterQuery extends API.FilterQuery<number> {}

    export interface FilterQuery<TType> extends API.FilterQuery<TType> {}

    export interface Filter<TValue = any> {
        api: (filter: FilterValue<TValue>) => FilterQuery<TValue>;
    }

    export interface FilterApply<TValue = any, TItem = any> {
        applyFilter: (value: TValue, item: TItem) => boolean;
    }

    export interface MaskFilter extends Filter {
        dict: string;
    }

    export interface FilterWithApply<
        TValue = any,
        TItem = any
    > extends Filter<TValue>, FilterApply<TValue, TItem> {}

    export interface MaskFilterWithApply<
        TValue = any,
        TItem = any
    > extends MaskFilter, FilterApply<TValue, TItem> {}

    export type MaskFilterApiValue = {
        value: string[];
        dict: FilterDictionary;
    };
}

export const maskFilterApi = (key: string) =>
    ({ value, dict }: Filters.MaskFilterApiValue): Filters.MaskFilterQuery => ({
        [key]: value.reduce((mask, filter) => mask + dict[filter]?.Mask || 0, 0),
    });

const isMaskFilter =
    (filter: Filters.Filter): filter is Filters.MaskFilter => 'dict' in filter;

export const mapFiltersToApi = fp.curry((
    values: Common.Catalog,
    filters: Common.Catalog<Filters.Filter>,
    dictionaries: Common.Catalog<Filters.FilterDictionary>,
): API.FilterQuery => fp.pipe(
    fp.keys,
    fp.map(f => {
        const filter = filters[f];
        return values[f] ? filter.api({
            value: values[f],
            ...isMaskFilter(filter) && {
                dict: dictionaries[filter.dict],
            },
        }) : null;
    }),
    fp.compact,
    fp.reduce(fp.assign, {}),
)(values));

export const applyFiltersLocal = (
    entity: any,
    filters: Common.Catalog<Filters.FilterWithApply>,
    values: Common.Catalog,
): boolean => fp.pipe(
    fp.toPairs,
    fp.all(([type, value]) => filters[type].applyFilter(value, entity)),
)(values);

export const mapDefaultFilters = (filters: string[]) => filters.reduce(
    (filtersObject, filter) => ({
        ...filtersObject,
        [filter]: {
            api: ({ value }) => ({
                [filter]: value,
            }),
        },
    }),
    {},
);

export const mapCheckboxesFilters = (filters: [string, string][]) => filters.reduce(
    (filtersObject, [field, filter]) => ({
        ...filtersObject,
        [field]: {
            api: ({ value }) => ({
                [filter]: fp.compact(Object.keys(value).map(s => value[s] && s)).join(',') || undefined,
            }),
        },
    }),
    {},
);

export const arrayCheckboxesFilters = (filters: [string, string][]) => filters.reduce(
    (filtersObject, [field, filter]) => ({
        ...filtersObject,
        [field]: {
            api: ({ value }) => ({
                [filter]: fp.compact(Object.keys(value).map(s => value[s] && s)) || undefined,
            }),
        },
    }),
    {},
);
